import { gql } from '@apollo/client';
import { Button, Form, Input, Select, Space, Switch } from 'antd';
import { useMutation } from 'hooks/apollo';
import React from 'react';
import useDataSource from 'hooks/useDataSource';
import useGlobalContext from 'hooks/useGlobalContext';
import globalMessages from 'translations/messages';

import Page from 'components/Page';
import { PeriodType } from 'shared/prisma/types';
import LicenseTag from 'components/LicenseTag';
import FormLabel from 'components/FormLabel';
import useFormatMessage from 'hooks/useFormatMessage';
import messages from '../messages';
import { useAccessibilityReview } from './context';
import {
  UPDATE_ACCESSIBILITY_AUTOMATIC_RUNS,
  UPDATE_ACCESSIBILITY_PDF_SCANNING,
  UPDATE_WEBSITE_BROKEN_LINKS,
} from './graphql';

const FIELDS = `
  id
  checkInterval
  checkReportEmail
`;

const extraFields = `
  website {
    ${FIELDS}
  }
`;

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      ${FIELDS}
    }
  }
`;

const CheckToggle = ({ id, label, description, checked, disabled, onChange }) => (
  <div>
    <Space align="center" wrap>
      <Switch
        id={id}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <label htmlFor={id}>{label}</label>
    </Space>
    <div>{description}</div>
  </div>
);

const BrokenLinksSettings = () => {
  const { scope, website, refetch } = useAccessibilityReview();
  const { dataSource, loading, getDataSourceLicenseSetting } = useDataSource({ extraFields });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE);
  const [updateAutomaticRuns, { loading: updatingAutomaticRuns }] = useMutation(UPDATE_ACCESSIBILITY_AUTOMATIC_RUNS);
  const [updatePdfScanning, { loading: updatingPdfScanning }] = useMutation(UPDATE_ACCESSIBILITY_PDF_SCANNING);
  const [updateBrokenLinks, { loading: updatingBrokenLinks }] = useMutation(UPDATE_WEBSITE_BROKEN_LINKS);
  const { message } = useGlobalContext();
  const $t = useFormatMessage();
  const saving = updating || updatingAutomaticRuns || updatingPdfScanning || updatingBrokenLinks;

  const onSubmit = async (values) => {
    try {
      await updateWebsite({
        variables: {
          id: dataSource?.website.id,
          input: {
            checkInterval: values.checkInterval || null,
            checkReportEmail: values.checkReportEmail || null,
          },
        },
      });
      if (scope && dataSource?.id) {
        await updateAutomaticRuns({
          variables: { dataSourceId: dataSource.id, automaticRunsEnabled: Boolean(values.automaticRunsEnabled) },
        });
        await updatePdfScanning({
          variables: { dataSourceId: dataSource.id, pdfScanningEnabled: Boolean(values.pdfScanningEnabled) },
        });
        if (website?.id) {
          await updateBrokenLinks({
            variables: {
              id: website.id,
              input: { brokenLinksEnabled: Boolean(values.brokenLinksEnabled) },
            },
          });
        }
        await refetch();
      }
      message.success($t(globalMessages.successfullFormFeedback));
    } catch (e) {
      console.log(e);
      message.error($t(globalMessages.errorFormFeedback));
    }
  };

  const hasWeeklyOption = getDataSourceLicenseSetting('brokenLinksWeeklyCheck');
  const hasMonthlyOption = getDataSourceLicenseSetting('brokenLinksMonthlyCheck');

  const checkIntervalOptions = [
    { value: '', label: 'Never' },
    { value: PeriodType.Week,
      label: (
        <Space>
          Weekly
          {!hasWeeklyOption && <LicenseTag setting="brokenLinksWeeklyCheck" showTooltip popoverProps={{ placement: 'left' }} />}
        </Space>
      ),
      disabled: !hasWeeklyOption },
    { value: PeriodType.Month,
      label: (
        <Space>
          Monthly
          {!hasMonthlyOption && <LicenseTag setting="brokenLinksMonthlyCheck" showTooltip popoverProps={{ placement: 'left' }} />}
        </Space>
      ),
      disabled: !hasMonthlyOption },
  ];

  const initialValues = {
    checkInterval: dataSource?.website?.checkInterval || '',
    checkReportEmail: dataSource?.website?.checkReportEmail || '',
    automaticRunsEnabled: Boolean(scope?.automaticRunsEnabled),
    pdfScanningEnabled: Boolean(scope?.pdfScanningEnabled),
    brokenLinksEnabled: website?.brokenLinksEnabled !== false,
  };

  if (loading && !dataSource?.website) return null;

  return (
    <Page.Content title="Scheduled check">
      <Form
        key={`${dataSource?.website?.id || ''}:${scope?.id || ''}`}
        onFinish={onSubmit}
        initialValues={initialValues}
        layout="vertical"
      >
        <Form.Item
          name="checkInterval"
          label="Automatically run checks"
          extra="Applies to Extellio website checks and Squidler accessibility scans."
        >
          <Select options={checkIntervalOptions} />
        </Form.Item>
        {scope && (
          <Space direction="vertical" size="small" style={{ display: 'flex', marginBottom: 24, maxWidth: 560 }}>
            <Form.Item name="automaticRunsEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <CheckToggle
                id="accessibility-check"
                label="Accessibility"
                description="Scan with Squidler for accessibility issues."
                disabled={!dataSource?.id || saving}
              />
            </Form.Item>
            <Form.Item name="pdfScanningEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <CheckToggle
                id="accessibility-pdf-scanning"
                label="Scan PDF documents"
                description="Automatically analyse discoverable PDF documents, including files that download instead of opening in the browser."
                disabled={saving}
              />
            </Form.Item>
            <Form.Item name="brokenLinksEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <CheckToggle
                id="accessibility-broken-links"
                label="Broken links"
                description="Find broken links during Extellio website checks."
                disabled={!website?.id || saving}
              />
            </Form.Item>
          </Space>
        )}
        <Form.Item name="checkReportEmail" label={<FormLabel label={messages.brokenLinkEmail} description={messages.brokenLinkEmailDescription} />}>
          <Input />
        </Form.Item>
        <Page.Footer>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
        </Page.Footer>
      </Form>
    </Page.Content>
  );
};

export default BrokenLinksSettings;
